import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonSpinner } from '@ionic/angular/standalone';
import { AccountProfile, AccountService, UpdateProfileRequest } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';

@Component({
  selector: 'app-profile-page',
  standalone: true,
  imports: [FormsModule, IonSpinner, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './profile-page.component.html',
  styleUrls: ['./profile-page.component.scss']
})
export class ProfilePageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  profile: AccountProfile | null = null;
  profileForm = {
    name: '',
    nickname: '',
    productAnalyticsEnabled: true
  };

  isInitialLoading = true;
  hasInitialLoadError = false;
  isSavingProfile = false;
  profileMessage = '';
  profileError = '';

  constructor(
    private readonly accountService: AccountService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadProfile();
  }

  saveProfile(): void {
    this.profileMessage = '';
    this.profileError = '';
    this.isSavingProfile = true;

    const payload: UpdateProfileRequest = {
      name: this.toNullable(this.profileForm.name),
      nickname: this.toNullable(this.profileForm.nickname),
      productAnalyticsEnabled: this.profileForm.productAnalyticsEnabled
    };

    this.accountService.updateProfile(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedProfile) => {
          this.runInAngular(() => {
            this.profile = updatedProfile;
            this.profileForm = {
              name: updatedProfile.name || '',
              nickname: updatedProfile.nickname || '',
              productAnalyticsEnabled: updatedProfile.productAnalyticsEnabled
            };
            this.profileMessage = 'Profile updated successfully.';
            this.isSavingProfile = false;
            const currentUser = this.authService.currentUser;
            if (currentUser) {
              this.authService.setCurrentUser({
                ...currentUser,
                name: updatedProfile.name,
                nickname: updatedProfile.nickname,
                productAnalyticsEnabled: updatedProfile.productAnalyticsEnabled,
                updatedAt: updatedProfile.updatedAt,
              });
            }
          });
        },
        error: (error: { message?: string }) => {
          this.runInAngular(() => {
            this.profileError = error.message || 'Could not update profile.';
            this.isSavingProfile = false;
          });
        }
      });
  }

  loadProfile(): void {
    this.isInitialLoading = true;
    this.hasInitialLoadError = false;
    this.profileError = '';

    this.accountService.getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          this.runInAngular(() => {
            this.profile = profile;
            this.profileForm = {
              name: profile.name || '',
              nickname: profile.nickname || '',
              productAnalyticsEnabled: profile.productAnalyticsEnabled
            };
            this.isInitialLoading = false;
            this.hasInitialLoadError = false;
          });
        },
        error: (error: { message?: string }) => {
          this.runInAngular(() => {
            this.profileError = error.message || 'Failed to load account details.';
            this.isInitialLoading = false;
            this.hasInitialLoadError = true;
          });
        }
      });
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }

  private toNullable(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
}
